import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useWiki, WikiSettings, WikiArticle, WikiSpec, WikiPros, WikiPerformance, defaultWikiSettings } from '@/context/WikiContext';

export default function WikiAdminTab() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const { settings: globalSettings, updateSettings, resetSettings } = useWiki();
  const [settings, setSettings] = useState<WikiSettings>(globalSettings);
  const [activeArticleIdx, setActiveArticleIdx] = useState(0);
  const [saved, setSaved] = useState(false);

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const inputCls = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[var(--accent)]'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[var(--accent)]';
  const fieldCls = `w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`;
  const labelCls = `block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`;
  const subBg = dark ? 'bg-[#0D1B2A]' : 'bg-gray-50';

  const article = settings.articles[activeArticleIdx];

  const setArticle = (updated: WikiArticle) => {
    setSettings((prev) => {
      const articles = [...prev.articles];
      articles[activeArticleIdx] = updated;
      return { ...prev, articles };
    });
  };

  const updateSpec = (i: number, field: keyof WikiSpec, val: string) => {
    const specs = [...article.specs];
    specs[i] = { ...specs[i], [field]: val };
    setArticle({ ...article, specs });
  };

  const addSpec = () => setArticle({ ...article, specs: [...article.specs, { label: '', value: '' }] });
  const removeSpec = (i: number) => setArticle({ ...article, specs: article.specs.filter((_, idx) => idx !== i) });

  const updatePros = (i: number, val: string) => {
    const pros = [...article.pros];
    pros[i] = { text: val };
    setArticle({ ...article, pros });
  };
  const addPro = () => setArticle({ ...article, pros: [...article.pros, { text: '' }] });
  const removePro = (i: number) => setArticle({ ...article, pros: article.pros.filter((_, idx) => idx !== i) });

  const updateCons = (i: number, val: string) => {
    const cons = [...article.cons];
    cons[i] = { text: val };
    setArticle({ ...article, cons });
  };
  const addCon = () => setArticle({ ...article, cons: [...article.cons, { text: '' }] });
  const removeCon = (i: number) => setArticle({ ...article, cons: article.cons.filter((_, idx) => idx !== i) });

  const updateUseCase = (i: number, val: string) => {
    const useCases = [...article.useCases];
    useCases[i] = val;
    setArticle({ ...article, useCases });
  };
  const updatePerformance = (key: keyof WikiPerformance, val: string) => {
    const numeric = Number(val);
    const nextValue = Number.isFinite(numeric) ? Math.max(0, Math.min(100, numeric)) : 0;
    setArticle({
      ...article,
      performance: {
        ...article.performance,
        [key]: nextValue,
      },
    });
  };
  const addUseCase = () => setArticle({ ...article, useCases: [...article.useCases, ''] });
  const removeUseCase = (i: number) => setArticle({ ...article, useCases: article.useCases.filter((_, idx) => idx !== i) });

  const addArticle = () => {
    const newArticle: WikiArticle = {
      id: `article-${Date.now()}`,
      slug: `article-${Date.now()}`,
      title: 'New Technology',
      subtitle: 'Subtitle here',
      badge: 'New',
      badgeColor: '#6366F1',
      icon: 'ri-lightbulb-line',
      heroImage: '',
      summary: '',
      body: '',
      specs: [],
      pros: [],
      cons: [],
      useCases: [],
      performance: {
        cycleLife: 50,
        weight: 50,
        voltage: 50,
        cost: 50,
      },
      published: false,
    };
    setSettings((prev) => ({ ...prev, articles: [...prev.articles, newArticle] }));
    setActiveArticleIdx(settings.articles.length);
  };

  const removeArticle = (i: number) => {
    setSettings((prev) => ({ ...prev, articles: prev.articles.filter((_, idx) => idx !== i) }));
    setActiveArticleIdx(Math.max(0, i - 1));
  };

  const handleSave = () => {
    updateSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    resetSettings();
    setSettings(defaultWikiSettings);
    setActiveArticleIdx(0);
  };

  return (
    <div className="space-y-5">
      {/* Page settings */}
      <div className={`${card} rounded-lg p-6 space-y-4`}>
        <h2 className={`font-bold text-sm ${text}`}>
          <i className="ri-book-2-line mr-2" style={{ color: 'var(--accent)' }}></i>
          Wiki Page Settings
        </h2>
        <div>
          <label className={labelCls}>Page Title</label>
          <input className={fieldCls} value={settings.pageTitle} onChange={(e) => setSettings((p) => ({ ...p, pageTitle: e.target.value }))} />
        </div>
        <div>
          <label className={labelCls}>Page Subtitle</label>
          <input className={fieldCls} value={settings.pageSubtitle} onChange={(e) => setSettings((p) => ({ ...p, pageSubtitle: e.target.value }))} />
        </div>
      </div>

      {/* Articles */}
      <div className="flex flex-col lg:flex-row gap-5">
        {/* Article list */}
        <div className="lg:w-52 shrink-0 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <p className={`text-xs font-bold uppercase tracking-widest ${muted}`}>Articles</p>
            <button
              onClick={addArticle}
              className="flex items-center gap-1 text-xs font-bold cursor-pointer whitespace-nowrap"
              style={{ color: 'var(--accent)' }}
            >
              <i className="ri-add-line"></i> Add
            </button>
          </div>
          {settings.articles.map((a, i) => (
            <div
              key={a.id}
              className={`flex items-center gap-2 p-3 rounded-xl cursor-pointer transition-all ${
                activeArticleIdx === i
                  ? 'text-white'
                  : dark ? 'text-white/50 hover:bg-white/5' : 'text-gray-500 hover:bg-gray-50'
              }`}
              style={activeArticleIdx === i ? { background: `${a.badgeColor}25`, color: a.badgeColor } : {}}
              onClick={() => setActiveArticleIdx(i)}
            >
              <div
                className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
                style={{ background: activeArticleIdx === i ? a.badgeColor : `${a.badgeColor}20` }}
              >
                <i className={`${a.icon} text-sm`} style={{ color: activeArticleIdx === i ? 'white' : a.badgeColor }}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate">{a.title}</p>
                <p className={`text-xs ${dark ? 'text-white/30' : 'text-gray-400'}`}>{a.published ? 'Published' : 'Draft'}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Article editor */}
        {article && (
          <div className="flex-1 space-y-4">
            <div className={`${card} rounded-lg p-5 space-y-4`}>
              <div className="flex items-center justify-between">
                <h3 className={`font-bold text-sm ${text}`}>Editing: {article.title}</h3>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${muted}`}>Published</span>
                    <button
                      onClick={() => setArticle({ ...article, published: !article.published })}
                      className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${article.published ? 'bg-emerald-500' : dark ? 'bg-white/20' : 'bg-gray-200'}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${article.published ? 'left-5' : 'left-0.5'}`}></span>
                    </button>
                  </div>
                  {settings.articles.length > 1 && (
                    <button
                      onClick={() => removeArticle(activeArticleIdx)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-500/10 cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Basic info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Title</label>
                  <input className={fieldCls} value={article.title} onChange={(e) => setArticle({ ...article, title: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Subtitle</label>
                  <input className={fieldCls} value={article.subtitle} onChange={(e) => setArticle({ ...article, subtitle: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>Badge Label</label>
                  <input className={fieldCls} value={article.badge} onChange={(e) => setArticle({ ...article, badge: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Badge Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={article.badgeColor} onChange={(e) => setArticle({ ...article, badgeColor: e.target.value })} className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent" />
                    <input className={`${fieldCls} flex-1`} value={article.badgeColor} onChange={(e) => setArticle({ ...article, badgeColor: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Icon (Remix Icon)</label>
                  <div className="flex items-center gap-2">
                    <input className={`${fieldCls} flex-1`} value={article.icon} onChange={(e) => setArticle({ ...article, icon: e.target.value })} placeholder="ri-flashlight-line" />
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg shrink-0" style={{ background: `${article.badgeColor}20` }}>
                      <i className={`${article.icon} text-lg`} style={{ color: article.badgeColor }}></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hero image */}
              <div>
                <label className={labelCls}>Hero Image URL</label>
                <input className={fieldCls} value={article.heroImage} onChange={(e) => setArticle({ ...article, heroImage: e.target.value })} placeholder="https://..." />
                {article.heroImage && (
                  <div className="mt-2 w-full h-28 rounded-lg overflow-hidden">
                    <img src={article.heroImage} alt="preview" className="w-full h-full object-cover object-top" />
                  </div>
                )}
              </div>

              {/* Summary */}
              <div>
                <label className={labelCls}>Summary (shown as intro)</label>
                <textarea rows={3} className={fieldCls} value={article.summary} onChange={(e) => setArticle({ ...article, summary: e.target.value })} />
              </div>

              {/* Body */}
              <div>
                <label className={labelCls}>Body Text (separate paragraphs with blank line)</label>
                <textarea rows={6} className={fieldCls} value={article.body} onChange={(e) => setArticle({ ...article, body: e.target.value })} />
              </div>
            </div>

            {/* Specs */}
            <div className={`${card} rounded-lg p-5`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`font-bold text-sm ${text}`}>Technical Specs</h3>
                <button onClick={addSpec} className="flex items-center gap-1 text-xs font-bold cursor-pointer whitespace-nowrap" style={{ color: 'var(--accent)' }}>
                  <i className="ri-add-line"></i> Add Spec
                </button>
              </div>
              <div className="space-y-2">
                {article.specs.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input className={`${fieldCls} flex-1`} value={s.label} onChange={(e) => updateSpec(i, 'label', e.target.value)} placeholder="Label (e.g. Cycle Life)" />
                    <input className={`${fieldCls} flex-1`} value={s.value} onChange={(e) => updateSpec(i, 'value', e.target.value)} placeholder="Value (e.g. 2000+)" />
                    <button onClick={() => removeSpec(i)} className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-500/10 cursor-pointer shrink-0">
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Pros & Cons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`${card} rounded-lg p-5`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm text-emerald-400">Advantages</h3>
                  <button onClick={addPro} className="flex items-center gap-1 text-xs font-bold text-emerald-400 cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line"></i> Add
                  </button>
                </div>
                <div className="space-y-2">
                  {article.pros.map((p, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input className={`${fieldCls} flex-1`} value={p.text} onChange={(e) => updatePros(i, e.target.value)} placeholder="Advantage..." />
                      <button onClick={() => removePro(i)} className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-500/10 cursor-pointer shrink-0">
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className={`${card} rounded-lg p-5`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm text-red-400">Limitations</h3>
                  <button onClick={addCon} className="flex items-center gap-1 text-xs font-bold text-red-400 cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line"></i> Add
                  </button>
                </div>
                <div className="space-y-2">
                  {article.cons.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input className={`${fieldCls} flex-1`} value={c.text} onChange={(e) => updateCons(i, e.target.value)} placeholder="Limitation..." />
                      <button onClick={() => removeCon(i)} className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-500/10 cursor-pointer shrink-0">
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Use cases */}
            <div className={`${card} rounded-lg p-5`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`font-bold text-sm ${text}`}>Best Use Cases</h3>
                <button onClick={addUseCase} className="flex items-center gap-1 text-xs font-bold cursor-pointer whitespace-nowrap" style={{ color: 'var(--accent)' }}>
                  <i className="ri-add-line"></i> Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {article.useCases.map((uc, i) => (
                  <div key={i} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${subBg}`}>
                    <input
                      className={`bg-transparent text-xs font-semibold outline-none w-32 ${text}`}
                      value={uc}
                      onChange={(e) => updateUseCase(i, e.target.value)}
                    />
                    <button onClick={() => removeUseCase(i)} className="w-4 h-4 flex items-center justify-center text-red-400 cursor-pointer shrink-0">
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Performance ratings */}
            <div className={`${card} rounded-lg p-5`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`font-bold text-sm ${text}`}>Performance Ratings</h3>
                <p className={`text-xs ${muted}`}>0 to 100 scale shown in Tech Wiki performance tab</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {([
                  { key: 'cycleLife', label: 'Cycle Life' },
                  { key: 'weight', label: 'Weight Efficiency' },
                  { key: 'voltage', label: 'Voltage Stability' },
                  { key: 'cost', label: 'Cost Efficiency' },
                ] as { key: keyof WikiPerformance; label: string }[]).map((metric) => (
                  <div key={metric.key}>
                    <label className={labelCls}>{metric.label}</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className={fieldCls}
                      value={article.performance[metric.key]}
                      onChange={(e) => updatePerformance(metric.key, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save */}
      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          onClick={handleReset}
          className={`flex items-center gap-2 font-bold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm border ${dark ? 'border-white/10 text-white/40 hover:text-white/70' : 'border-gray-200 text-gray-400 hover:text-gray-600'}`}
        >
          <i className="ri-refresh-line"></i> Reset to Default
        </button>
        <div className="flex items-center gap-3">
          {saved && (
            <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
              <i className="ri-check-line"></i> Wiki saved!
            </div>
          )}
          <button
            onClick={handleSave}
            className="flex items-center gap-2 text-white font-bold px-6 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
            style={{ background: 'var(--accent)' }}
          >
            <i className="ri-save-line"></i> Save &amp; Publish
          </button>
        </div>
      </div>
    </div>
  );
}
